import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Phone, Mail } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";

const STATUS_LABELS = {
    new: "Nouă",
    contacted: "Contactat",
    closed: "Finalizată",
};
const STATUS_COLORS = {
    new: "bg-[#d4af37]/15 text-[#8a6f1f] border-[#d4af37]/40",
    contacted: "bg-[#0d3d2e]/10 text-[#0d3d2e] border-[#0d3d2e]/30",
    closed: "bg-neutral-200 text-neutral-600 border-neutral-300",
};

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const adminEmail = typeof window !== "undefined" ? localStorage.getItem("os_email") : "";

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter !== "all" ? { params: { status: filter } } : {};
            const [list, st] = await Promise.all([
                api.get("/admin/quote-requests", params),
                api.get("/admin/stats"),
            ]);
            setItems(list.data);
            setStats(st.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("os_token");
                navigate("/admin");
                return;
            }
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filter, navigate]);

    useEffect(() => {
        if (!localStorage.getItem("os_token")) {
            navigate("/admin");
            return;
        }
        fetchData();
    }, [fetchData, navigate]);

    const changeStatus = async (id, status) => {
        try {
            await api.patch(`/admin/quote-requests/${id}`, { status });
            toast.success("Status actualizat");
            fetchData();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm("Sigur vrei să ștergi această cerere?")) return;
        try {
            await api.delete(`/admin/quote-requests/${id}`);
            toast.success("Cerere ștearsă");
            fetchData();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const logout = () => {
        localStorage.removeItem("os_token");
        localStorage.removeItem("os_email");
        navigate("/admin");
    };

    return (
        <div className="min-h-screen bg-[#fcfbf8]">
            {/* Header */}
            <header className="bg-[#0d3d2e] text-[#fcfbf8]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src="/icon-192.png"
                            alt="OPTIM SAFE"
                            className="h-9 w-auto object-contain"
                        />
                        <div className="text-[10px] uppercase tracking-[0.16em] text-[#fcfbf8]/60">{adminEmail}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchData}
                            data-testid="admin-refresh-btn"
                            className="inline-flex items-center gap-2 text-sm text-[#fcfbf8]/85 hover:text-[#fcfbf8]"
                        >
                            <RefreshCw className="h-4 w-4" strokeWidth={1.5} /> Reîmprospătează
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-[#fcfbf8]/30 px-4 py-1.5 text-sm hover:bg-[#fcfbf8]/10"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Deconectare
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                        { k: "Total", v: stats.total, f: "all" },
                        { k: "Noi", v: stats.new, f: "new" },
                        { k: "Contactate", v: stats.contacted, f: "contacted" },
                        { k: "Finalizate", v: stats.closed, f: "closed" },
                    ].map((s) => (
                        <button
                            key={s.k}
                            onClick={() => setFilter(s.f)}
                            data-testid={`stat-${s.f}`}
                            className={`text-left border rounded-xl p-5 transition-all ${
                                filter === s.f
                                    ? "border-[#0d3d2e] bg-[#0d3d2e] text-[#fcfbf8]"
                                    : "border-[#0d3d2e]/15 bg-white hover:border-[#0d3d2e]/40"
                            }`}
                        >
                            <div className={`text-xs uppercase tracking-[0.16em] ${filter === s.f ? "text-[#fcfbf8]/70" : "text-[#0d3d2e]/60"}`}>
                                {s.k}
                            </div>
                            <div className="font-display text-4xl mt-2">{s.v}</div>
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="bg-white border border-[#0d3d2e]/15 rounded-xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-[#0d3d2e]/10 flex justify-between items-center">
                        <h2 className="font-display text-2xl text-[#0d3d2e]">Cereri de ofertă</h2>
                        <span className="text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/55">
                            {items.length} {items.length === 1 ? "rezultat" : "rezultate"}
                        </span>
                    </div>

                    {loading ? (
                        <div className="p-10 text-center text-[#0d3d2e]/60">Se încarcă...</div>
                    ) : items.length === 0 ? (
                        <div className="p-12 text-center text-[#0d3d2e]/60" data-testid="admin-empty">
                            Nicio cerere pentru filtrul curent.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[#f5f3ec] text-[#0d3d2e]/70 uppercase text-xs tracking-[0.14em]">
                                    <tr>
                                        <th className="text-left px-5 py-3">Data</th>
                                        <th className="text-left px-5 py-3">Nume</th>
                                        <th className="text-left px-5 py-3">Contact</th>
                                        <th className="text-left px-5 py-3">Produs</th>
                                        <th className="text-left px-5 py-3">Mesaj</th>
                                        <th className="text-left px-5 py-3">Status</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((r) => (
                                        <tr key={r.id} className="border-t border-[#0d3d2e]/10 align-top" data-testid={`row-${r.id}`}>
                                            <td className="px-5 py-4 text-[#0d3d2e]/70 whitespace-nowrap">{formatDate(r.created_at)}</td>
                                            <td className="px-5 py-4 font-medium text-[#0d3d2e]">{r.name}</td>
                                            <td className="px-5 py-4">
                                                <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 text-[#0d3d2e] hover:text-[#0d3d2e]/70">
                                                    <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {r.phone}
                                                </a>
                                                <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-[#0d3d2e]/70 hover:text-[#0d3d2e] mt-1">
                                                    <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {r.email}
                                                </a>
                                                {r.contact_method && (
                                                    <div className="text-xs text-[#0d3d2e]/55 mt-1" data-testid={`contact-method-${r.id}`}>
                                                        Preferă: {r.contact_method}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-[#0d3d2e]">{r.product_type}</td>
                                            <td className="px-5 py-4 max-w-xs text-[#0d3d2e]/75">
                                                {r.message || <span className="text-[#0d3d2e]/40">—</span>}
                                            </td>
                                            <td className="px-5 py-4">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            data-testid={`status-trigger-${r.id}`}
                                                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium ${STATUS_COLORS[r.status]}`}
                                                        >
                                                            {STATUS_LABELS[r.status]} ▾
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                                            <DropdownMenuItem key={key} onClick={() => changeStatus(r.id, key)} data-testid={`status-set-${key}-${r.id}`}>
                                                                {label}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <button
                                                    onClick={() => deleteItem(r.id)}
                                                    data-testid={`delete-${r.id}`}
                                                    className="text-[#0d3d2e]/50 hover:text-red-600 transition-colors"
                                                    title="Șterge"
                                                >
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
